import React, { useEffect, useState, useTransition } from "react";
import ReactDOM from "react-dom";
import { hrefForUserProfile } from "@/utils/username";
import Image from "@/components/AppImage/AppImage";
import homeIcon from "../../assets/home.svg";
import messagesIcon from "../../assets/messages.svg";
import plusIcon from "../../assets/plus.svg";
import alarmBellIcon from "../../assets/alarm-bell.svg";
import questionIcon from "../../assets/question.svg";
import logoIcon from "../../assets/logo.svg";
import arrowBackIcon from "../../assets/arrow-back.svg";
import dashboardIcon from "../../assets/dashboard.svg";
import phoneIcon from "../../assets/phone-retro.svg";
import itinerariesIcon from "../../assets/itineraries.svg";
// Web nav-rail-only icon set.
import railLogoIcon from "../../assets/images/logo-ellipse.png"; // compact pin logomark (@3x raster, sharper than the raster-embedded SVG)
import railHomeIcon from "../../assets/nav-item-discover.svg";
import railBookmarkIcon from "../../assets/nav-item-saved.svg";
import railNotificationsIcon from "../../assets/nav-item-notifications.svg";
import railTripsIcon from "../../assets/nav-item-trips.svg";
import railSearchIcon from "../../assets/search.svg";
import loginIcon from "../../assets/avatar.svg";
import railCreateBtnIcon from "../../assets/create-action-btn.svg"; // bordered square + plus
import railMoreIcon from "../../assets/images/sidebar-icon-wrapper.png"; // hamburger icon for the More button
import AppVersionLabel from "@/components/AppVersionLabel/AppVersionLabel";
import railPerformanceIcon from "../../assets/nav-item-performance.svg";
import railRequestsIcon from "../../assets/nav-item-requests.svg";
// More-card icon set.
import moreHotelBookingsIcon from "../../assets/hotel-icon.png";
import moreAdminIcon from "../../assets/more-admin-icon.svg";
import moreFaqIcon from "../../assets/images/faq_icon.png";
import moreTermsIcon from "../../assets/images/terms_conditions_icon.png";
import morePrivacyIcon from "../../assets/images/privacy_policy_icon.png";
import moreLogoutIcon from "../../assets/logout-icon.png";
import AccountTypeSection from "@/components/EditProfile/AccountTypeSection";
import {
  TRIP_IMPORT_ENTRY_SUBTITLE,
  TRIP_IMPORT_ENTRY_TITLE,
} from '@/constants/tripImportEntryCopy';
import {
  PinIcon,
  DocIcon,
  LinkIcon,
  ListIcon,
  TripIcon,
  SocialStackIcon,
} from "@/components/MobileTabBar/MobileCreateMenuIcons";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { EXPLORE_ITEMS } from "@/constants/exploreItems";
import NavFlyoutPanel from "./NavFlyoutPanel";
import GlobeThriversTermsContent from "@/components/Legal/GlobeThriversTermsContent";
import GlobeThriversPrivacyContent from "@/components/Legal/GlobeThriversPrivacyContent";
import AppDownload from "../AppDownload/AppDownload";
import SignUpCTAPopover from "../SignUpCTAPopover/SignUpCTAPopover";
import LoginModal from "../LoginModal/LoginModal";
import ComingSoonPopover from "../ComingSoonPopover/ComingSoonPopover";
import NotificationsPanel from "../Notifications/Notifications";
import ImportInspoModal from "@/components/blocks/ImportInspoModal";
import CreateListModal from "@/components/CreateListModal/CreateListModal";
import CreateItineraryModal from "@/components/CreateItineraryModal/CreateItineraryModal";
import RecommendFromPostsFlowModal from "@/components/RecommendFromPostsFlowModal/RecommendFromPostsFlowModal";
import CreateNewLinkFlow from "@/components/LinkPerformance/CreateNewLinkFlow";
import Spinner from "@/components/Spinner/Spinner";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import "./Navigation.css";
import { useAuth, useNotificationsBadge } from "@/contexts";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";
import { isUserAdmin } from "@/utils/cognitoGroups";
import { isCreatorOrAdvisor } from "@/utils/isCreatorOrAdvisor";
import { useAdvisorProMenuState } from "@/hooks/useIsAdvisorProActive";
import { useTravelMenuActions } from "@/hooks/useTravelMenuActions";
import { useAuthAction } from "@/hooks/useAuthAction";
import { useAvatarImage } from "@/hooks/useAvatarImage";
import { WebAnalytics } from "@/lib/analytics";
import { EVENT_CREATE_LIST_BUTTON_CLICKED, EVENT_RECOMMEND_PLACE_BUTTON_CLICKED } from "@/lib/analyticsEvents";
import { useOpenTripImportRoute } from "@/hooks/useOpenTripImportRoute";
import { useOpenInspoImportRoute } from "@/hooks/useOpenInspoImportRoute";
import { useOpenAccountSettings } from "@/hooks/useOpenAccountSettings";
import { useBackHandler } from "@/lib/backHandlerRegistry";
import { debug } from "@/utils/logger";
import { getCityFreeformSearchTerm } from "@/utils/cityUtils";

interface Props {
  isVisible?: boolean;
  onClose?: () => void;
}

const exploreIconStyle = { fontSize: 24 } as const;
const exploreIconClassName = "text-orange-500";

// Icon per EXPLORE_ITEMS id, styled to match the Create flyout's icon set.
const EXPLORE_ITEM_ICONS: Record<string, React.FC> = {
  creators: () => <PeopleOutlinedIcon sx={exploreIconStyle} className={exploreIconClassName} aria-hidden />,
  destinations: () => <PinIcon />,
  "travel-styles": () => <AutoAwesomeOutlinedIcon sx={exploreIconStyle} className={exploreIconClassName} aria-hidden />,
  "trending-trips": () => <TrendingUpOutlinedIcon sx={exploreIconStyle} className={exploreIconClassName} aria-hidden />,
  advisors: () => <SupportAgentOutlinedIcon sx={exploreIconStyle} className={exploreIconClassName} aria-hidden />,
};

interface CreateFlyoutItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

// One row of the desktop Create flyout: orange icon tile + title/description.
const CreateFlyoutItem: React.FC<CreateFlyoutItemProps> = ({ icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-2xl px-2 py-3 text-left transition-colors hover:bg-gray-100"
  >
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50">
      {icon}
    </span>
    <span className="flex flex-col">
      <span className="font-manrope text-[15px] font-semibold leading-[20px] text-gray-900">{title}</span>
      <span className="font-manrope text-[13px] leading-[18px] text-gray-500">{description}</span>
    </span>
  </button>
);

const CreateFlyoutSectionHeader: React.FC<{ title: string; className?: string }> = ({ title, className = "pt-2" }) => (
  <div className={`px-2 pb-2 font-manrope text-[15px] font-bold text-gray-900 ${className}`}>{title}</div>
);

const Navigation: React.FC<Props> = ({ isVisible = false, onClose }) => {
  const pathname = useLocation().pathname;
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { unreadCount: notificationsUnreadCount } = useNotificationsBadge();
  const isAdmin = isUserAdmin();
  const isCreator = isCreatorOrAdvisor(user);
  // `user` from auth is cached at login and can be stale after checkout
  // redirects, so fetch Advisor Pro state live for menu labels/routes.
  const isAdvisorAccount = user?.profile_type === 'advisor';
  const advisorProMenuState = useAdvisorProMenuState(isAdvisorAccount);
  const showAdvisorProEntry = isAdvisorAccount;
  const showAdvisorProSetup = Boolean(
    advisorProMenuState?.isActive && advisorProMenuState.hasPendingOnboarding
  );
  const advisorProEntryPath = showAdvisorProSetup ? "/advisor-pro/onboarding" : "/advisor-pro";
  const advisorProEntryLabel = !advisorProMenuState
    ? "Advisor Pro"
    : showAdvisorProSetup
      ? "Finish Pro Setup"
      : advisorProMenuState.isActive
        ? "Manage Advisor Pro"
        : "Upgrade to Advisor Pro";
  const advisorProEntryHighlighted = !advisorProMenuState
    || !advisorProMenuState.isActive
    || showAdvisorProSetup
    || advisorProMenuState.verificationStatus === "rejected";
  // Creator-only Create flyout layout (Creator Quick Links + merged Create section).
  const isCreatorCreateMenu = Boolean(user) && isCreator;
  const closeDrawer = () => {
    if (onClose) onClose();
  };
  const {
    handleBookClick,
    handleHotelBookingsClick,
    handlePerformanceClick,
    isOpeningDashboard,
    isBookWhitelabelLoading,
  } = useTravelMenuActions({ onBeforeNavigate: closeDrawer });

  const isMobileViewport = useIsMobileViewport();
  const desktop = !isMobileViewport;
  const { requireAuth, showPopover, getPopoverProps, showLoginModal, setShowLoginModal, loginModalRedirectPath, handleSignUpClick } = useAuthAction();

  // Hardware back closes the nav drawer instead of leaving the page.
  useBackHandler(() => { if (onClose) onClose(); }, isVisible);
  const [isPendingNav, _startNavTransition] = useTransition();
  const [signUpPopoverPath, _setSignUpPopoverPath] = useState<string | undefined>(undefined);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCommunityPopover, setShowCommunityPopover] = useState(false);
  const [showImportInspoModal, setShowImportInspoModal] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showCreateItineraryModal, setShowCreateItineraryModal] = useState(false);
  const [createItineraryOpenAddMode, setCreateItineraryOpenAddMode] = useState<boolean | 'social' | 'copy-paste' | 'search'>(false);
  /**
   * Which tab to auto-open on the Add Places modal after list creation.
   * 'copy-paste' for Paste-text import; 'search' for pancake Create List; false to skip.
   */
  const [createListOpenAddMode, setCreateListOpenAddMode] = useState<'copy-paste' | 'search' | false>(false);
  const [showRecommendFromPostsFlow, setShowRecommendFromPostsFlow] = useState(false);
  const [showCreateLinkFlow, setShowCreateLinkFlow] = useState(false);
  const openInspoImportRoute = useOpenInspoImportRoute();
  const openTripImportRoute = useOpenTripImportRoute();
  const openAccountSettings = useOpenAccountSettings();
  // Desktop/tablet rail flyouts: Home opens Explore, "+" opens Create, hamburger opens the More overflow.
  const [isExploreMenuOpen, setIsExploreMenuOpen] = useState(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  // The Account Type dialog renders inside the "More" popover, so closing the
  // popover would unmount it. Hold the popover open while the dialog is up.
  const [isAccountTypeDialogOpen, setIsAccountTypeDialogOpen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [loginModalInitialMode, setLoginModalInitialMode] = useState<'login' | 'signup'>('signup');
  const resolvedAvatarImage = useAvatarImage({
    largeAvatar: typeof user?.large_avatar_url === 'string' ? user.large_avatar_url : undefined,
    smallAvatar: typeof user?.small_avatar_url === 'string' ? user.small_avatar_url : undefined,
    avatarUrl: typeof user?.avatar_url === 'string' ? user.avatar_url : undefined,
    profileImageUrl: typeof user?.profile_image_url === 'string' ? user.profile_image_url : undefined,
  }) as string;
  useEffect(() => {
    if (!isMoreMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape closes the Account Type dialog first; it owns its own handler.
      if (e.key === "Escape" && !isAccountTypeDialogOpen) setIsMoreMenuOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMoreMenuOpen, isAccountTypeDialogOpen]);

  const closeRailFlyouts = () => {
    setIsExploreMenuOpen(false);
    setIsCreateMenuOpen(false);
    setIsMoreMenuOpen(false);
    setShowNotifications(false);
  };

  // Mirrors ExploreHeader's handleExploreToggleClick/handleExploreItemClick so the
  // rail's Home icon opens the identical Explore content, just in the flyout skin.
  const handleExploreToggle = () => {
    if (user) {
      setIsCreateMenuOpen(false);
      setIsMoreMenuOpen(false);
      setShowNotifications(false);
      setIsExploreMenuOpen((prev) => !prev);
      return;
    }
    requireAuth('explore', () => {}, undefined, { surface: 'explore_header' });
  };

  const handleExploreItemClick = (path: string) => {
    if (!user) return;
    setIsExploreMenuOpen(false);
    router(path);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    
    // Handle paths with query parameters
    if (path.includes('?')) {
      const [pathPart, queryPart] = path.split('?');
      if (pathname !== pathPart) return false;
      
      // Parse query parameters from both path and current URL
      const pathParams = new URLSearchParams(queryPart);
      const currentParams = searchParams;
      
      // Check if all query params in path match current URL params
      for (const [key, value] of pathParams.entries()) {
        if (currentParams.get(key) !== value) {
          return false;
        }
      }
      return true;
    }
    
    // Simple pathname comparison for paths without query params
    return pathname === path;
  };

  const handleNavLinkClick = (path?: string) => {
    if (onClose) onClose();
    if (!path) return;
    
    // Home and FAQ are always accessible (public routes)
    if (path === '/' || path === '/faq') {
      window.location.href = path;
      return;
    }
    
    // For other routes, require auth
    requireAuth('post', () => {
      window.location.href = path;
    });
  };

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      // Show sign-up popover for unauthenticated users
      requireAuth('community', () => {
        // This won't execute for unauthenticated users
      });
    } else {
      // Show coming soon popover for authenticated users
      setShowCommunityPopover(true);
    }
  };

  const handleCreateListNew = () => {
    requireAuth('post', () => {
      WebAnalytics.trackContentCreation(EVENT_CREATE_LIST_BUTTON_CLICKED, {
        source_page: pathname || 'unknown',
        flow: 'create-list',
      });
      if (onClose) onClose();
      setCreateListOpenAddMode('search');
      setShowCreateListModal(true);
    });
  };

  const handleCreateListFromInspo = () => {
    setShowImportInspoModal(false);
    setCreateListOpenAddMode('search');
    setShowCreateListModal(true);
  };

  const handleCreateTripFromInspo = () => {
    setShowImportInspoModal(false);
    setCreateItineraryOpenAddMode('search');
    setShowCreateItineraryModal(true);
  };

  const handleSocialImportFromInspo = () => {
    setCreateItineraryOpenAddMode('social');
    setShowCreateItineraryModal(true);
  };

  const closeCreateListModal = () => {
    setShowCreateListModal(false);
    setCreateListOpenAddMode(false);
  };

  const createListModalPortal =
    showCreateListModal && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <CreateListModal
            onClose={closeCreateListModal}
            onListCreated={closeCreateListModal}
            openAddModeOnCreate={createListOpenAddMode}
          />,
          document.body
        )
      : null;

  const recommendFromPostsPortal =
    showRecommendFromPostsFlow && typeof document !== "undefined"
      ? ReactDOM.createPortal(
          <RecommendFromPostsFlowModal
            isOpen
            onClose={() => setShowRecommendFromPostsFlow(false)}
          />,
          document.body
        )
      : null;

  const createNewLinkPortal =
    showCreateLinkFlow && typeof document !== "undefined"
      ? ReactDOM.createPortal(
          <CreateNewLinkFlow
            isOpen
            onClose={() => setShowCreateLinkFlow(false)}
          />,
          document.body
        )
      : null;

  // Creator Quick Links (creators/advisors only).
  const handleCreateNewLink = () => {
    requireAuth("post", () => {
      if (onClose) onClose();
      setShowCreateLinkFlow(true);
    });
  };

  const handleMyLinks = () => {
    requireAuth("post", () => {
      if (onClose) onClose();
      router("/performance/link");
    });
  };

  const handleRecommendPlaceFromPosts = () => {
    requireAuth("post", () => {
      WebAnalytics.trackContentCreation(EVENT_RECOMMEND_PLACE_BUTTON_CLICKED, {
        source_page: pathname || "unknown",
        flow: "recommend-place-from-posts-nav",
      });
      if (onClose) onClose();
      setShowRecommendFromPostsFlow(true);
    });
  };

  const handleCreateItinerary = () => {
    requireAuth('post', () => {
      if (onClose) onClose();
      setCreateItineraryOpenAddMode('search');
      setShowCreateItineraryModal(true);
    });
  };

  const closeCreateItineraryModal = () => {
    setShowCreateItineraryModal(false);
    setCreateItineraryOpenAddMode(false);
  };

  const createItineraryModalPortal =
    showCreateItineraryModal && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <CreateItineraryModal
            onClose={closeCreateItineraryModal}
            onTripCreated={closeCreateItineraryModal}
            openAddModeOnCreate={createItineraryOpenAddMode}
          />,
          document.body
        )
      : null;

  const handleInspoToTrip = () => {
    requireAuth('post', () => {
      if (onClose) onClose();
      openInspoImportRoute();
    });
  };

  const handleTripImport = () => {
    requireAuth('post', () => {
      if (onClose) onClose();
      openTripImportRoute({ entry: 'create_flyout' });
    });
  };

  const handleTripImportFromInspo = () => {
    requireAuth('post', () => {
      if (onClose) onClose();
      openTripImportRoute({ entry: 'start_building' });
    });
  };

  const handleNotificationsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    requireAuth('notifications', () => {
      setShowNotifications(true);
      if (onClose) onClose();
    });
  };

  const handleMyTripsClick = () => {
    requireAuth('itineraries', () => {
      if (onClose) onClose();
      router('/my-travel');
    });
  };

  const handleSearchClick = () => {
    const cityName = getCityFreeformSearchTerm(user?.city) ?? '';
    const resultsHref = cityName ? `/results?search=${encodeURIComponent(cityName)}` : '/results';
    requireAuth('search', () => {
      if (onClose) onClose();
      router(resultsHref);
    });
  };

  const handleSavedClick = () => {
    requireAuth('saved', () => {
      if (onClose) onClose();
      router('/saved');
    });
  };

  const handleNavBookClick = () => {
    requireAuth('book', () => {
      handleBookClick('navigation_menu');
    });
  };

  const handleNavHotelBookingsClick = () => {
    requireAuth('profile', () => {
      handleHotelBookingsClick();
    });
  };

  const handleNavPerformanceClick = () => {
    requireAuth('profile', () => {
      handlePerformanceClick();
    });
  };

  const handleNavRequestsClick = () => {
    requireAuth('profile', () => {
      if (onClose) onClose();
      router('/requests');
    });
  };

  // Desktop rail "More" panel handlers.
  const handleEditProfileClick = () => {
    requireAuth('profile', () => {
      router(user?._id ? hrefForUserProfile({ _id: user._id, username: user.username, profile_type: user.profile_type }) : '/profile');
    });
  };

  const handleNavLogoutClick = async () => {
    try {
      await logout();
      router('/');
    } catch (err) {
      debug('[Navigation] Logout error:', err);
    }
  };

  if (desktop) {
    return (
      <>
        {createListModalPortal}
        {createItineraryModalPortal}
        {recommendFromPostsPortal}
        {createNewLinkPortal}

        <nav className="nav-container nav-rail">
          <div className="logo">
            <img
              src={railLogoIcon}
              alt="Logo"
              className="nav-rail-logo"
              onClick={() => { closeRailFlyouts(); handleNavLinkClick("/"); }}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <div className="nav-divider" aria-hidden="true"></div>
          <ul className="nav-list nav-rail-primary">
            <li className="nav-item">
              <div
                className={`nav-link ${
                  isActive("/") ? "active" : ""
                } cursor-pointer`}
                onClick={handleExploreToggle}
                title="Explore"
              >
                <img src={railHomeIcon} alt="Explore" className="nav-icon" />
              </div>
            </li>
            <li className="nav-item">
              <div
                className={`nav-link ${pathname === '/saved' || pathname.startsWith('/saved/') ? 'active' : ''} cursor-pointer`}
                onClick={() => { closeRailFlyouts(); handleSavedClick(); }}
                title="Saved"
              >
                <img src={railBookmarkIcon} alt="Saved" className="nav-icon" />
              </div>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className="nav-create-btn"
                onClick={() => { setIsExploreMenuOpen(false); setIsMoreMenuOpen(false); setShowNotifications(false); setIsCreateMenuOpen((prev) => !prev); }}
                aria-label="Create"
                title="Create"
              >
                <img src={railCreateBtnIcon} alt="" />
              </button>
            </li>
            <li className="nav-item">
              <div
                className={`nav-link ${pathname === '/my-travel' || pathname.startsWith('/my-travel/') ? 'active' : ''} cursor-pointer`}
                onClick={() => { closeRailFlyouts(); handleMyTripsClick(); }}
                title="My Trips"
              >
                <img src={railTripsIcon} alt="My Trips" className="nav-icon" />
              </div>
            </li>
            <li className="nav-item">
              <div
                className={`nav-link ${pathname === '/results' || pathname.startsWith('/results/') ? 'active' : ''} cursor-pointer`}
                onClick={() => { closeRailFlyouts(); handleSearchClick(); }}
                title="Search"
              >
                <img src={railSearchIcon} alt="Search" className="nav-icon nav-search-icon" />
              </div>
            </li>
            {isCreator && user && (
              <li className="nav-item">
                <div
                  className={`nav-link ${pathname === '/performance' || pathname.startsWith('/performance/') ? 'active' : ''} cursor-pointer`}
                  onClick={() => { closeRailFlyouts(); handleNavPerformanceClick(); }}
                  title="Performance"
                >
                  <img src={railPerformanceIcon} alt="Performance" className="nav-icon" />
                </div>
              </li>
            )}
            {isAdvisorAccount && user && (
              <li className="nav-item">
                <div
                  className={`nav-link ${pathname === '/requests' || pathname.startsWith('/requests/') ? 'active' : ''} cursor-pointer`}
                  onClick={() => { closeRailFlyouts(); handleNavRequestsClick(); }}
                  title="Requests"
                >
                  <img src={railRequestsIcon} alt="Requests" className="nav-icon" />
                </div>
              </li>
            )}
            <li className="nav-item">
              <button
                className="nav-link nav-link-notifications"
                onClick={(e) => { closeRailFlyouts(); handleNotificationsClick(e); }}
                title="Notifications"
              >
                <span className="nav-icon-badge-wrap">
                  <img
                    src={railNotificationsIcon}
                    alt="Notifications"
                    className="nav-icon"
                  />
                  {notificationsUnreadCount > 0 && (
                    <span className="nav-notifications-badge" aria-label={`${notificationsUnreadCount} unread notifications`}>
                      {notificationsUnreadCount > 99 ? "99+" : notificationsUnreadCount}
                    </span>
                  )}
                </span>
              </button>
            </li>
          </ul>

          <div className="nav-rail-spacer" aria-hidden="true"></div>

          {user ? (
            <button
              type="button"
              className="nav-rail-avatar-btn"
              onClick={() => { closeRailFlyouts(); handleEditProfileClick(); }}
              aria-label="My Profile"
              title="My Profile"
            >
              <Image
                src={resolvedAvatarImage}
                alt={user.name || 'User'}
                className="nav-rail-avatar-img"
                width={35}
                height={35}
              />
            </button>
          ) : (
            <button
              type="button"
              className="nav-rail-login-btn"
              onClick={() => { closeRailFlyouts(); setLoginModalInitialMode('login'); handleSignUpClick(pathname || '/'); }}
              aria-label="Log In"
              title="Log In"
            >
              <img src={loginIcon} alt="" className="nav-icon" />
            </button>
          )}

          <button
            type="button"
            className="nav-link nav-more-btn"
            onClick={() => { setIsExploreMenuOpen(false); setIsCreateMenuOpen(false); setShowNotifications(false); setIsMoreMenuOpen((prev) => !prev); }}
            aria-label="More"
            title="More"
          >
            <img src={railMoreIcon} alt="" className="nav-icon" />
          </button>
        </nav>

        <NavFlyoutPanel title="Explore" isOpen={isExploreMenuOpen} onClose={() => setIsExploreMenuOpen(false)}>
          <div className="px-4 pb-6">
            <div className="flex flex-col gap-1">
              {EXPLORE_ITEMS.map((item) => {
                const Icon = EXPLORE_ITEM_ICONS[item.id] ?? PinIcon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleExploreItemClick(item.path)}
                    className="group flex w-full items-center gap-4 rounded-2xl px-2 py-3 text-left transition-colors hover:bg-gray-100"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50">
                      <Icon />
                    </span>
                    <span className="flex flex-col">
                      <span className="font-manrope text-[15px] font-semibold leading-[20px] text-gray-900">{item.title}</span>
                      <span className="font-manrope text-[13px] leading-[18px] text-gray-500">{item.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </NavFlyoutPanel>

        <NavFlyoutPanel title="Create" isOpen={isCreateMenuOpen} onClose={() => setIsCreateMenuOpen(false)}>
          <div className="px-4 pb-6">
            {/* Creators/advisors lead with their link tools, and their
                recommendation flow lives there as "Create New Link" instead of
                under Create. Everyone else keeps the Import / Create split. */}
            {isCreatorCreateMenu && (
              <>
                <CreateFlyoutSectionHeader title="Creator Quick Links" />
                <div className="flex flex-col gap-1">
                  <CreateFlyoutItem
                    icon={<PinIcon />}
                    title="Create New Link"
                    description="Recommend a place & earn when someone books"
                    onClick={() => { setIsCreateMenuOpen(false); handleCreateNewLink(); }}
                  />
                  <CreateFlyoutItem
                    icon={<LinkIcon />}
                    title="My Links"
                    description="View & share your links"
                    onClick={() => { setIsCreateMenuOpen(false); handleMyLinks(); }}
                  />
                </div>
                <div className="mx-2 my-4 h-px bg-[#F9F5F1]" aria-hidden="true" />
              </>
            )}

            <CreateFlyoutSectionHeader
              title={isCreatorCreateMenu ? "Create" : "Import"}
              className={isCreatorCreateMenu ? "pt-0" : "pt-2"}
            />
            <div className="flex flex-col gap-1">
              <CreateFlyoutItem
                icon={<SocialStackIcon />}
                title="Import from Social"
                description="AI saves & organizes places"
                onClick={() => { setIsCreateMenuOpen(false); handleInspoToTrip(); }}
              />
              <CreateFlyoutItem
                icon={<DocIcon />}
                title={TRIP_IMPORT_ENTRY_TITLE}
                description={TRIP_IMPORT_ENTRY_SUBTITLE}
                onClick={() => { setIsCreateMenuOpen(false); handleTripImport(); }}
              />
            </div>

            {!isCreatorCreateMenu && <CreateFlyoutSectionHeader title="Create" className="pt-5" />}
            <div className={`flex flex-col gap-1 ${isCreatorCreateMenu ? "pt-1" : ""}`}>
              {!isCreatorCreateMenu && (
                <CreateFlyoutItem
                  icon={<PinIcon />}
                  title="Recommendation"
                  description="Save & share one place"
                  onClick={() => { setIsCreateMenuOpen(false); handleRecommendPlaceFromPosts(); }}
                />
              )}
              <CreateFlyoutItem
                icon={<ListIcon />}
                title="List"
                description="Organize your recommendations"
                onClick={() => { setIsCreateMenuOpen(false); handleCreateListNew(); }}
              />
              <CreateFlyoutItem
                icon={<TripIcon />}
                title="Trip"
                description="Build an itinerary"
                onClick={() => { setIsCreateMenuOpen(false); handleCreateItinerary(); }}
              />
            </div>
          </div>
        </NavFlyoutPanel>

        {isMoreMenuOpen && (
          <>
            {/* The Account Type dialog renders inside this popover, so the popover
                cannot unmount while it is open — it is hidden instead. `hidden`
                would detach it from the a11y tree along with the dialog, so the
                backdrop and panel are visually removed and made inert instead. */}
            <div
              className="fixed inset-0 z-[1000]"
              onClick={() => setIsMoreMenuOpen(false)}
              aria-hidden="true"
              style={isAccountTypeDialogOpen ? { display: 'none' } : undefined}
            />
            <div
              className="fixed z-[1001] flex flex-col bg-white py-3 shadow-card-drop"
              style={{
                left: 92,
                bottom: 16,
                width: 280,
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
                borderRadius: 16,
                // Keep it mounted (the dialog lives inside) but out of the way.
                ...(isAccountTypeDialogOpen
                  ? { opacity: 0, pointerEvents: 'none' as const }
                  : { opacity: 1 }),
              }}
              role="dialog"
              aria-modal={isAccountTypeDialogOpen ? undefined : true}
              aria-label="More"
            >
              {user && (
                <div className="px-5 py-3">
                  <AccountTypeSection
                    size="md"
                    onOverlayOpenChange={(open) => {
                      setIsAccountTypeDialogOpen(open);
                      // Closing the dialog returns to the page, not to a popover
                      // the user can no longer see behind it.
                      if (!open) setIsMoreMenuOpen(false);
                    }}
                  />
                </div>
              )}

              {/* Advisors always get a visible Advisor Pro entry to avoid
                  state-blind navigation. */}
              {showAdvisorProEntry && (
                <button
                  type="button"
                  onClick={() => { setIsMoreMenuOpen(false); handleNavLinkClick(advisorProEntryPath); }}
                  className={`flex w-full items-center gap-[14px] h-[52px] px-5 text-left transition-colors ${
                    advisorProEntryHighlighted
                      ? "bg-[#E7E7FF] hover:bg-[#dcdcff]"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"
                        stroke="#371168"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <path d="M19 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8z" stroke="#371168" strokeWidth="2" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className={`font-manrope text-[15px] font-semibold leading-[18px] ${
                    advisorProEntryHighlighted ? "text-[#371168]" : "text-gray-900"
                  }`}>
                    {advisorProEntryLabel}
                  </span>
                </button>
              )}

              {user && (
                <button
                  type="button"
                  className={`flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-gray-100 ${isOpeningDashboard ? 'opacity-50' : ''}`}
                  onClick={() => { setIsMoreMenuOpen(false); handleNavHotelBookingsClick(); }}
                  disabled={isOpeningDashboard}
                >
                  <img src={moreHotelBookingsIcon} alt="" className="h-6 w-6 shrink-0" />
                  <span className="font-manrope text-[15px] font-medium leading-[20px] text-gray-900">Hotel Bookings</span>
                </button>
              )}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => { setIsMoreMenuOpen(false); handleNavLinkClick("/admin"); }}
                  className="flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-gray-100"
                >
                  <img src={moreAdminIcon} alt="" className="h-6 w-6 shrink-0" />
                  <span className="font-manrope text-[15px] font-medium leading-[20px] text-gray-900">Admin</span>
                </button>
              )}
              {user && (
                <button
                  type="button"
                  onClick={() => { setIsMoreMenuOpen(false); openAccountSettings('nav_more_desktop'); }}
                  className="flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-gray-100"
                >
                  <ManageAccountsOutlinedIcon sx={{ fontSize: 24 }} className="shrink-0 text-gray-900" aria-hidden />
                  <span className="font-manrope text-[15px] font-medium leading-[20px] text-gray-900">Account Settings</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => { setIsMoreMenuOpen(false); handleNavLinkClick("/faq"); }}
                className="flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-gray-100"
              >
                <img src={moreFaqIcon} alt="" className="h-6 w-6 shrink-0" />
                <span className="font-manrope text-[15px] font-medium leading-[20px] text-gray-900">FAQ&apos;s</span>
              </button>

              <div className="my-0.5 h-px w-full shrink-0 bg-[#EEEEEE]" aria-hidden="true" />

              <button
                type="button"
                onClick={() => { setIsMoreMenuOpen(false); setShowTermsModal(true); }}
                className="flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-gray-100"
              >
                <img src={moreTermsIcon} alt="" className="h-6 w-6 shrink-0" />
                <span className="font-manrope text-[15px] font-medium leading-[20px] text-gray-900">Terms &amp; Conditions</span>
              </button>

              <div className="my-0.5 h-px w-full shrink-0 bg-[#EEEEEE]" aria-hidden="true" />

              <button
                type="button"
                onClick={() => { setIsMoreMenuOpen(false); setShowPrivacyModal(true); }}
                className="flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-gray-100"
              >
                <img src={morePrivacyIcon} alt="" className="h-6 w-6 shrink-0" />
                <span className="font-manrope text-[15px] font-medium leading-[20px] text-gray-900">Privacy Policy</span>
              </button>

              {user && (
                <>
                  <div className="my-0.5 h-[2px] w-full shrink-0 bg-[#EEEEEE]" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => { setIsMoreMenuOpen(false); handleNavLogoutClick(); }}
                    className="flex w-full items-center gap-3 h-[52px] px-5 text-left transition-colors hover:bg-red-50"
                  >
                    <img src={moreLogoutIcon} alt="" className="h-6 w-6 shrink-0" />
                    <span className="font-manrope text-[15px] font-medium leading-[20px] text-red-500">Log out</span>
                  </button>
                </>
              )}

              <AppVersionLabel className="shrink-0 px-5 pt-2" />
            </div>
          </>
        )}

        {showTermsModal && (
          <div
            className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowTermsModal(false)}
          >
            <div
              className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-lora text-xl font-semibold text-gray-900">Terms &amp; Conditions</h3>
                <button
                  type="button"
                  className="text-2xl leading-none text-gray-500 hover:text-gray-900"
                  onClick={() => setShowTermsModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="font-manrope text-[14px] leading-[1.5] text-gray-700 [&_h4]:mt-4 [&_h4]:font-semibold [&_h4]:text-gray-900 [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5">
                <GlobeThriversTermsContent />
              </div>
            </div>
          </div>
        )}

        {showPrivacyModal && (
          <div
            className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowPrivacyModal(false)}
          >
            <div
              className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-lora text-xl font-semibold text-gray-900">Privacy Policy</h3>
                <button
                  type="button"
                  className="text-2xl leading-none text-gray-500 hover:text-gray-900"
                  onClick={() => setShowPrivacyModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="font-manrope text-[14px] leading-[1.5] text-gray-700 [&_h4]:mt-4 [&_h4]:font-semibold [&_h4]:text-gray-900 [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5">
                <GlobeThriversPrivacyContent />
              </div>
            </div>
          </div>
        )}
        {showPopover && (() => {
          const popoverProps = getPopoverProps();
          if (!popoverProps) return null;
          return (
            <SignUpCTAPopover
              title={popoverProps.title}
              description={popoverProps.description}
              onClose={popoverProps.onClose}
              redirectPath={signUpPopoverPath}
              onSignUpClick={() => handleSignUpClick(signUpPopoverPath)}
            />
          );
        })()}
        {showLoginModal && (
          <LoginModal
            onClose={() => { setShowLoginModal(false); setLoginModalInitialMode('signup'); }}
            redirectPath={loginModalRedirectPath}
            initialMode={loginModalInitialMode}
          />
        )}
        {showCommunityPopover && (
          <ComingSoonPopover
            onClose={() => setShowCommunityPopover(false)}
            featureType="community"
          />
        )}
        <NotificationsPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          entryPoint="navigation_menu"
        />
        {showImportInspoModal && typeof document !== 'undefined' && ReactDOM.createPortal(
          <ImportInspoModal
            isOpen={showImportInspoModal}
            onClose={() => setShowImportInspoModal(false)}
            onCreateListClick={handleCreateListFromInspo}
            onCreateTripClick={handleCreateTripFromInspo}
            onSocialImportClick={handleSocialImportFromInspo}
            onTripImportClick={handleTripImportFromInspo}
          />,
          document.body
        )}
        {isPendingNav && typeof document !== 'undefined' && ReactDOM.createPortal(
          <Spinner />,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      {createListModalPortal}
      {createItineraryModalPortal}
      {recommendFromPostsPortal}
      {isVisible && <div className="nav-overlay" onClick={onClose}></div>}

      <nav className={`nav-container ${isVisible ? "visible" : ""}`}>
        <div className="logo">
          <Image 
            src={logoIcon} 
            alt="Logo" 
            className="logo-image" 
            priority
            width={150}
            height={50}
            style={{ objectFit: 'contain' }}
          />
          <button className="nav-close-btn" onClick={onClose} aria-label="Close menu">
            <img src={arrowBackIcon} alt="" style={{ transform: 'rotate(180deg)' }} />
          </button>
        </div>

        <div className="nav-divider" aria-hidden="true"></div>

        <ul className="nav-list">
          <li className="nav-item">
            <div
              className={`nav-link ${
                isActive("/") ? "active" : ""
              } cursor-pointer`}
              onClick={() => handleNavLinkClick("/")}
            >
              <img src={homeIcon} alt="Explore" className="nav-icon" />
              Explore
            </div>
          </li>
          <li className="nav-item">
            <div
              className={`nav-link ${pathname === "/my-travel" || pathname.startsWith("/my-travel/") ? "active" : ""} cursor-pointer`}
              onClick={handleMyTripsClick}
            >
              <img src={itinerariesIcon} alt="My Trips" className="nav-icon" />
              My Trips
            </div>
          </li>
          <li className="nav-item">
            <div
              className={`nav-link ${pathname === "/results" || pathname.startsWith("/results/") ? "active" : ""} cursor-pointer`}
              onClick={handleSearchClick}
            >
              <img src={railSearchIcon} alt="Search" className="nav-icon nav-search-icon" />
              Search
            </div>
          </li>
          <li className="nav-item">
            <button className="nav-link nav-link-notifications" onClick={handleNotificationsClick}>
              <span className="nav-icon-badge-wrap">
                <img
                  src={alarmBellIcon}
                  alt="Notifications"
                  className="nav-icon"
                />
                {notificationsUnreadCount > 0 && (
                  <span className="nav-notifications-badge" aria-label={`${notificationsUnreadCount} unread notifications`}>
                    {notificationsUnreadCount > 99 ? "99+" : notificationsUnreadCount}
                  </span>
                )}
              </span>
              <span style={{ lineHeight: '0px', marginTop: "4px" }}>Notifications</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${showRecommendFromPostsFlow ? "active" : ""}`}
              onClick={handleRecommendPlaceFromPosts}
            >
              <img src={plusIcon} alt="Recommend a Place" className="nav-icon" />
              <span className="nav-link-content">
                Recommend a Place
                <span className="nav-link-description">From your trips, saved posts, or search</span>
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${showCreateListModal || pathname === '/list/new' ? "active" : ""}`}
              onClick={handleCreateListNew}
            >
              <img src={plusIcon} alt="Create List" className="nav-icon" />
              <span className="nav-link-content">
                Create List
                <span className="nav-link-description">Build a collection of places</span>
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${showCreateItineraryModal ? "active" : ""}`}
              onClick={handleCreateItinerary}
            >
              <img src={plusIcon} alt="Create Trip" className="nav-icon" />
              <span className="nav-link-content">
                Create Trip
                <span className="nav-link-description">Build a multi-day trip</span>
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={handleInspoToTrip}>
              <img src={plusIcon} alt="Import from social" className="nav-icon" />
              <span className="nav-link-content">
                Import from social
                <span className="nav-link-description">Import from Instagram or TikTok</span>
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={handleTripImport}>
              <img src={plusIcon} alt={TRIP_IMPORT_ENTRY_TITLE} className="nav-icon" />
              <span className="nav-link-content">
                {TRIP_IMPORT_ENTRY_TITLE}
                <span className="nav-link-description">{TRIP_IMPORT_ENTRY_SUBTITLE}</span>
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={handleCommunityClick}>
              <img src={messagesIcon} alt="Messages" className="nav-icon" />
              Community
            </button>
          </li>
          {isCreator && user && (
            <li className="nav-item">
              <div
                className={`nav-link ${pathname === '/performance' || pathname.startsWith('/performance/') ? 'active' : ''} cursor-pointer`}
                onClick={handleNavPerformanceClick}
              >
                <img src={dashboardIcon} alt="Performance" className="nav-icon" />
                Performance
              </div>
            </li>
          )}
          {isAdvisorAccount && user && (
            <li className="nav-item">
              <div
                className={`nav-link ${pathname === '/requests' || pathname.startsWith('/requests/') ? 'active' : ''} cursor-pointer`}
                onClick={handleNavRequestsClick}
              >
                <img src={railRequestsIcon} alt="Requests" className="nav-icon" />
                Requests
              </div>
            </li>
          )}
          {user && (
            <li className="nav-item">
              <button
                className={`nav-link ${isBookWhitelabelLoading ? 'opacity-50' : ''}`}
                onClick={handleNavBookClick}
                disabled={isBookWhitelabelLoading}
              >
                <img src={phoneIcon} alt="Book" className="nav-icon" />
                {isBookWhitelabelLoading ? 'Opening…' : 'Book'}
              </button>
            </li>
          )}
          {user && (
            <li className="nav-item">
              <button
                className={`nav-link ${isOpeningDashboard ? 'opacity-50' : ''}`}
                onClick={handleNavHotelBookingsClick}
                disabled={isOpeningDashboard}
              >
                <img src={dashboardIcon} alt="Hotel Bookings" className="nav-icon" />
                Hotel Bookings
              </button>
            </li>
          )}
          {isAdmin && (
            <li className="nav-item">
              <div
                className={`nav-link ${
                  isActive("/admin") ? "active" : ""
                } cursor-pointer`}
                onClick={() => handleNavLinkClick("/admin")}
              >
                <img src={dashboardIcon} alt="Admin" className="nav-icon" />
                Admin
              </div>
            </li>
          )}
            <li className="nav-item">
              <div
                className={`nav-link ${
                  isActive("/faq") ? "active" : ""
                } cursor-pointer`}
                onClick={() => handleNavLinkClick("/faq")}
              >
                <img src={questionIcon} alt="FAQ" className="nav-icon" />
                FAQ
              </div>
            </li>
        </ul>

        <div className="bottom-section">
          <div className="download-card">
            <div className="download-title">Have GT at your fingertips!</div>
            <AppDownload />
          </div>
        </div>
      </nav>
      {showPopover && (() => {
        const popoverProps = getPopoverProps();
        if (!popoverProps) return null;
        return (
          <SignUpCTAPopover
            title={popoverProps.title}
            description={popoverProps.description}
            onClose={popoverProps.onClose}
            redirectPath={signUpPopoverPath}
            onSignUpClick={() => handleSignUpClick(signUpPopoverPath)}
          />
        );
      })()}
      {showCommunityPopover && (
        <ComingSoonPopover
          onClose={() => setShowCommunityPopover(false)}
          featureType="community"
        />
      )}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          redirectPath={loginModalRedirectPath}
          initialMode="signup"
        />
      )}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        entryPoint="navigation_menu"
      />
      {showImportInspoModal && typeof document !== 'undefined' && ReactDOM.createPortal(
        <ImportInspoModal
          isOpen={showImportInspoModal}
          onClose={() => setShowImportInspoModal(false)}
          onCreateListClick={handleCreateListFromInspo}
          onCreateTripClick={handleCreateTripFromInspo}
          onSocialImportClick={handleSocialImportFromInspo}
          onTripImportClick={handleTripImportFromInspo}
        />,
        document.body
      )}
      {isPendingNav && typeof document !== 'undefined' && ReactDOM.createPortal(
        <Spinner />,
        document.body
      )}
    </>
  );
};

export default Navigation;
